import React, { useEffect, useMemo, useState } from 'react';
import API from './api';

const UI = {
  card: 'rounded-2xl border border-slate-200 bg-white shadow-sm',
  cardSoft: 'rounded-2xl border border-slate-200/80 bg-white/95 shadow-sm',
  input:
    'w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50 disabled:text-slate-400 disabled:border-slate-200',
  select:
    'w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50 disabled:text-slate-400 disabled:border-slate-200',
  textarea:
    'w-full min-h-[110px] rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50 disabled:text-slate-400 disabled:border-slate-200',
  label: 'mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-slate-500',
  th: 'px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500',
  td: 'px-4 py-2.5 align-top text-sm text-slate-700',
  primaryBtn:
    'inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60',
};

function Badge({ children, className = '' }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-5 ${className}`}
    >
      {children}
    </span>
  );
}

function BoolBadge({ value, yes = 'Enabled', no = 'Disabled' }) {
  return value ? (
    <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">
      {yes}
    </Badge>
  ) : (
    <Badge className="border-slate-200 bg-slate-50 text-slate-700">
      {no}
    </Badge>
  );
}

function TierBadge({ tier }) {
  const t = String(tier || '').toLowerCase();

  if (t === 'enterprise_ai') {
    return (
      <Badge className="border-purple-200 bg-purple-50 text-purple-700">
        Enterprise AI
      </Badge>
    );
  }
  if (t === 'guidance_plus_notes') {
    return (
      <Badge className="border-indigo-200 bg-indigo-50 text-indigo-700">
        Guidance + Notes
      </Badge>
    );
  }
  if (t === 'guidance_only') {
    return (
      <Badge className="border-sky-200 bg-sky-50 text-sky-700">
        Guidance Only
      </Badge>
    );
  }
  if (t === 'custom') {
    return (
      <Badge className="border-amber-200 bg-amber-50 text-amber-700">
        Custom
      </Badge>
    );
  }

  return (
    <Badge className="border-slate-200 bg-slate-50 text-slate-700">
      None
    </Badge>
  );
}

function StatCard({ label, value, valueClassName = 'text-slate-900' }) {
  return (
    <div className={`${UI.cardSoft} p-3.5`}>
      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className={`mt-1 text-xl font-bold ${valueClassName}`}>{value}</div>
    </div>
  );
}

function Switch({ checked, onChange, disabled = false, label, description }) {
  return (
    <label
      className={`flex items-start justify-between gap-4 rounded-2xl border px-4 py-3 transition ${
        disabled
          ? 'border-slate-200 bg-slate-50 opacity-70'
          : 'border-slate-200 bg-white hover:bg-slate-50/70'
      }`}
    >
      <div className="min-w-0">
        <div className={`text-sm font-medium ${disabled ? 'text-slate-500' : 'text-slate-800'}`}>
          {label}
        </div>
        {description ? (
          <div className={`mt-0.5 text-xs ${disabled ? 'text-slate-400' : 'text-slate-500'}`}>
            {description}
          </div>
        ) : null}
      </div>

      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition ${
          checked
            ? 'border-sky-600 bg-sky-600'
            : 'border-slate-300 bg-slate-200'
        } ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition ${
            checked ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </label>
  );
}

const emptyForm = {
  ai_enabled: false,
  ai_guidance_enabled: false,
  ai_notes_enabled: false,
  ai_nightly_guidance_enabled: false,
  ai_cache_enabled: true,
  ai_tier: 'none',
  ai_daily_limit: '',
  ai_monthly_limit: '',
  ai_status_reason: '',
};

export default function SuperAdminAiEntitlements() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadRows = async () => {
    setLoading(true);
    try {
      const res = await API.get('/super/organizations/ai-settings');
      const list = res.data?.rows || [];
      setRows(list);

      if (!selectedOrgId && list.length) {
        setSelectedOrgId(String(list[0].organization_id));
      }
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.error || 'Failed to load organizations AI settings');
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (orgId) => {
    if (!orgId) {
      setDetail(null);
      setForm(emptyForm);
      return;
    }

    setDetailLoading(true);
    try {
      const res = await API.get(`/super/organizations/${orgId}/ai-settings`);
      const row = res.data?.row || null;
      setDetail(row);

      setForm({
        ai_enabled: !!row?.ai_enabled,
        ai_guidance_enabled: !!row?.ai_guidance_enabled,
        ai_notes_enabled: !!row?.ai_notes_enabled,
        ai_nightly_guidance_enabled: !!row?.ai_nightly_guidance_enabled,
        ai_cache_enabled: row?.ai_cache_enabled == null ? true : !!row.ai_cache_enabled,
        ai_tier: row?.ai_tier || 'none',
        ai_daily_limit: row?.ai_daily_limit ?? '',
        ai_monthly_limit: row?.ai_monthly_limit ?? '',
        ai_status_reason: row?.ai_status_reason || '',
      });
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.error || 'Failed to load organization AI detail');
      setDetail(null);
      setForm(emptyForm);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedOrgId) {
      loadDetail(selectedOrgId);
    } else {
      setDetail(null);
      setForm(emptyForm);
    }
    // eslint-disable-next-line
  }, [selectedOrgId]);

  const stats = useMemo(() => {
    let enabled = 0;
    let guidance = 0;
    let notes = 0;
    let nightly = 0;

    for (const r of rows) {
      if (r.ai_enabled) enabled += 1;
      if (r.ai_guidance_enabled) guidance += 1;
      if (r.ai_notes_enabled) notes += 1;
      if (r.ai_nightly_guidance_enabled) nightly += 1;
    }

    return {
      total: rows.length,
      enabled,
      guidance,
      notes,
      nightly,
    };
  }, [rows]);

  const aiMasterEnabled = !!form.ai_enabled;
  const dependentDisabled = !aiMasterEnabled;

  const save = async () => {
    if (!selectedOrgId) return;

    setSaving(true);
    try {
      const res = await API.put(`/super/organizations/${selectedOrgId}/ai-settings`, form);
      const row = res.data?.row || null;

      if (row) {
        setDetail(row);
        setForm({
          ai_enabled: !!row?.ai_enabled,
          ai_guidance_enabled: !!row?.ai_guidance_enabled,
          ai_notes_enabled: !!row?.ai_notes_enabled,
          ai_nightly_guidance_enabled: !!row?.ai_nightly_guidance_enabled,
          ai_cache_enabled: row?.ai_cache_enabled == null ? true : !!row.ai_cache_enabled,
          ai_tier: row?.ai_tier || 'none',
          ai_daily_limit: row?.ai_daily_limit ?? '',
          ai_monthly_limit: row?.ai_monthly_limit ?? '',
          ai_status_reason: row?.ai_status_reason || '',
        });
      }

      await loadRows();
      alert('AI settings saved successfully.');
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.error || 'Failed to save AI settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 p-4 sm:p-5">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white shadow-lg">
        <div className="p-5 sm:p-6">
          <div className="max-w-3xl">
            <div className="inline-flex items-center rounded-full border border-white/10 bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-200">
              AI Entitlements
            </div>
            <h1 className="mt-3 text-xl font-bold tracking-tight sm:text-2xl">
              Organization AI Access
            </h1>
            <p className="mt-1.5 text-sm leading-6 text-slate-300">
              Control which organizations can access AI Guidance, AI Notes,
              caching, and nightly pre-generation.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <StatCard label="Organizations" value={stats.total} />
        <StatCard
          label="AI Enabled"
          value={stats.enabled}
          valueClassName="text-emerald-700"
        />
        <StatCard
          label="Guidance"
          value={stats.guidance}
          valueClassName="text-sky-700"
        />
        <StatCard
          label="Notes"
          value={stats.notes}
          valueClassName="text-indigo-700"
        />
        <StatCard
          label="Nightly Enabled"
          value={stats.nightly}
          valueClassName="text-purple-700"
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.2fr_1fr]">
        <div className={`${UI.card} overflow-hidden`}>
          <div className="border-b border-slate-200 px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-800">
              Organizations
            </h2>
          </div>

          <div className="p-4">
            <select
              className={UI.select}
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
            >
              <option value="">Select organization</option>
              {rows.map((r) => (
                <option key={r.organization_id} value={r.organization_id}>
                  {r.organization_name} (#{r.organization_id})
                </option>
              ))}
            </select>
          </div>

          <div className="overflow-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-slate-50/90">
                <tr>
                  <th className={UI.th}>Organization</th>
                  <th className={UI.th}>Tier</th>
                  <th className={UI.th}>AI</th>
                  <th className={UI.th}>Guidance</th>
                  <th className={UI.th}>Notes</th>
                  <th className={UI.th}>Nightly</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.organization_id}
                    className={`cursor-pointer border-t border-slate-200 hover:bg-slate-50 ${
                      String(r.organization_id) === String(selectedOrgId)
                        ? 'bg-sky-50'
                        : ''
                    }`}
                    onClick={() => setSelectedOrgId(String(r.organization_id))}
                  >
                    <td className={UI.td}>
                      <div className="font-semibold text-slate-800">
                        {r.organization_name}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        #{r.organization_id} · {r.timezone || '-'}
                      </div>
                    </td>
                    <td className={UI.td}>
                      <TierBadge tier={r.ai_tier} />
                    </td>
                    <td className={UI.td}>
                      <BoolBadge value={r.ai_enabled} />
                    </td>
                    <td className={UI.td}>
                      <BoolBadge value={r.ai_guidance_enabled} />
                    </td>
                    <td className={UI.td}>
                      <BoolBadge value={r.ai_notes_enabled} />
                    </td>
                    <td className={UI.td}>
                      <BoolBadge
                        value={r.ai_nightly_guidance_enabled}
                        yes="On"
                        no="Off"
                      />
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-400">
                      {loading ? 'Loading organizations...' : 'No organizations found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className={UI.card}>
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-800">
              AI Access Configuration
            </h2>
            {detail?.ai_tier ? <TierBadge tier={detail.ai_tier} /> : null}
          </div>

          <div className="space-y-5 p-5">
            {detailLoading ? (
              <div className="text-sm text-slate-500">
                Loading organization details...
              </div>
            ) : !detail ? (
              <div className="text-sm text-slate-400">Select an organization.</div>
            ) : (
              <>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="font-semibold text-slate-800">
                    {detail.organization_name}
                  </div>
                  <div className="mt-1 text-sm text-slate-500">
                    #{detail.organization_id} · {detail.timezone || '-'} ·{' '}
                    {detail.billing_plan || '-'} ·{' '}
                    {detail.subscription_status || '-'}
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                        Today usage
                      </div>
                      <div className="mt-1 text-lg font-bold text-slate-800">
                        {detail.usage?.daily_count ?? 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                        This month
                      </div>
                      <div className="mt-1 text-lg font-bold text-slate-800">
                        {detail.usage?.monthly_count ?? 0}
                      </div>
                    </div>
                  </div>
                </div>

                {!aiMasterEnabled && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    AI is currently disabled for this organization. Dependent AI settings remain visible for review, but cannot be edited until AI is enabled.
                  </div>
                )}

                <div className="grid gap-3">
                  <Switch
                    label="Enable AI"
                    description="Master organization-level AI access."
                    checked={form.ai_enabled}
                    onChange={(value) =>
                      setForm((s) => ({ ...s, ai_enabled: value }))
                    }
                  />

                  <Switch
                    label="Enable Guidance"
                    description="Allow AI-generated visit guidance."
                    checked={form.ai_guidance_enabled}
                    disabled={dependentDisabled}
                    onChange={(value) =>
                      setForm((s) => ({ ...s, ai_guidance_enabled: value }))
                    }
                  />

                  <Switch
                    label="Enable Notes"
                    description="Allow AI-generated note suggestions."
                    checked={form.ai_notes_enabled}
                    disabled={dependentDisabled}
                    onChange={(value) =>
                      setForm((s) => ({ ...s, ai_notes_enabled: value }))
                    }
                  />

                  <Switch
                    label="Enable Nightly Guidance"
                    description="Allow nightly pre-generation for guidance."
                    checked={form.ai_nightly_guidance_enabled}
                    disabled={dependentDisabled}
                    onChange={(value) =>
                      setForm((s) => ({
                        ...s,
                        ai_nightly_guidance_enabled: value,
                      }))
                    }
                  />

                  <Switch
                    label="Enable AI Cache"
                    description="Allow cached AI artifacts for reuse."
                    checked={form.ai_cache_enabled}
                    disabled={dependentDisabled}
                    onChange={(value) =>
                      setForm((s) => ({ ...s, ai_cache_enabled: value }))
                    }
                  />
                </div>

                <div className={`grid gap-4 ${dependentDisabled ? 'opacity-70' : ''}`}>
                  <div>
                    <label className={UI.label}>AI Tier</label>
                    <select
                      className={UI.select}
                      value={form.ai_tier}
                      disabled={dependentDisabled}
                      onChange={(e) =>
                        setForm((s) => ({ ...s, ai_tier: e.target.value }))
                      }
                    >
                      <option value="none">none</option>
                      <option value="guidance_only">guidance_only</option>
                      <option value="guidance_plus_notes">guidance_plus_notes</option>
                      <option value="enterprise_ai">enterprise_ai</option>
                      <option value="custom">custom</option>
                    </select>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className={UI.label}>Daily limit</label>
                      <input
                        className={UI.input}
                        value={form.ai_daily_limit}
                        disabled={dependentDisabled}
                        onChange={(e) =>
                          setForm((s) => ({
                            ...s,
                            ai_daily_limit: e.target.value,
                          }))
                        }
                        placeholder="e.g. 50"
                      />
                    </div>

                    <div>
                      <label className={UI.label}>Monthly limit</label>
                      <input
                        className={UI.input}
                        value={form.ai_monthly_limit}
                        disabled={dependentDisabled}
                        onChange={(e) =>
                          setForm((s) => ({
                            ...s,
                            ai_monthly_limit: e.target.value,
                          }))
                        }
                        placeholder="e.g. 1000"
                      />
                    </div>
                  </div>

                  <div>
                    <label className={UI.label}>Status reason</label>
                    <textarea
                      className={UI.textarea}
                      value={form.ai_status_reason}
                      disabled={dependentDisabled}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          ai_status_reason: e.target.value,
                        }))
                      }
                      placeholder="Optional internal note explaining why AI is enabled, limited, or disabled."
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <button onClick={save} disabled={saving} className={UI.primaryBtn}>
                    {saving ? 'Saving...' : 'Save AI Settings'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
